import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import Header from "@/components/landing/Header";
import AppStoreBadges from "@/components/landing/AppStoreBadges";
import { projectDividends, toCSV } from "@/lib/dividend-calc";
import {
  CUSTOM_RATE_CODE,
  DIVIDEND_TAX_RATES,
  getCurrencyFor,
  getRate,
  type Region,
} from "@/content/dividend-tax-rates";
import CalculatorForm, { type FormState } from "./CalculatorForm";
import ResultsTable from "./ResultsTable";
import ResultsChart from "./ResultsChart";
import FAQ, { type FAQItem } from "./FAQ";

const Footer = lazy(() => import("@/components/landing/Footer"));

type Locale = "en" | "de";

const DEFAULT_STATE: FormState = {
  initialInvestment: 10000,
  monthlyContribution: 300,
  startingYieldPct: 3.5,
  dividendGrowthPct: 6,
  priceGrowthPct: 6,
  years: 20,
  reinvest: true,
  country: "US",
  customRatePct: 15,
};

interface Props {
  /** Locale of the *URL* (not the user's chosen UI language). Used only for
   *  canonical / hreflang / og:url. The UI language is driven by i18next. */
  locale: Locale;
}

const CANONICAL: Record<Locale, string> = {
  en: "https://getpiefolio.com/tools/dividend-calculator",
  de: "https://getpiefolio.com/tools/dividenden-rechner",
};

const REGION_ORDER: Region[] = ["europe", "americas", "asiaPacific", "mea", "other"];

const DividendCalculatorPage = ({ locale }: Props) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const [state, setState] = useState<FormState>({
    ...DEFAULT_STATE,
    country: locale === "de" ? "DE" : "US",
  });

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      return new Intl.DisplayNames(["en"], { type: "region" });
    }
  }, [i18n.language]);
  const nameFor = (code: string) => {
    try {
      return regionNames.of(code) ?? code;
    } catch {
      return code;
    }
  };

  const rateInfo = getRate(state.country);
  const effectiveRate =
    state.country === CUSTOM_RATE_CODE ? state.customRatePct / 100 : rateInfo.rate;
  const currency =
    state.country === CUSTOM_RATE_CODE ? "EUR" : getCurrencyFor(state.country);

  const result = useMemo(
    () => projectDividends({ ...state, taxRate: effectiveRate }),
    [state, effectiveRate]
  );

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }),
    [currency, i18n.language]
  );
  const pctFmt = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language]
  );

  // Apply dir="rtl" to <html> while this page is mounted under Arabic.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.getAttribute("dir");
    if (isRtl) html.setAttribute("dir", "rtl");
    return () => {
      if (prev) html.setAttribute("dir", prev);
      else html.removeAttribute("dir");
    };
  }, [isRtl]);

  const handleCsv = () => {
    const csv = toCSV(result.rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dividend-projection-${state.country}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formLabels = {
    initial: t("calculator.form.initial"),
    monthly: t("calculator.form.monthly"),
    yield: t("calculator.form.yield"),
    divGrowth: t("calculator.form.divGrowth"),
    priceGrowth: t("calculator.form.priceGrowth"),
    years: t("calculator.form.years"),
    reinvest: t("calculator.form.reinvest"),
    country: t("calculator.form.country"),
  };
  const tableLabels = {
    year: t("calculator.results.table.year"),
    contributed: t("calculator.results.table.contributed"),
    portfolio: t("calculator.results.table.portfolio"),
    gross: t("calculator.results.table.gross"),
    net: t("calculator.results.table.net"),
    cumulative: t("calculator.results.table.cumulative"),
    yoc: t("calculator.results.table.yoc"),
  };
  const chartLabels = {
    portfolio: t("calculator.results.chart.portfolio"),
    cumulativeDividends: t("calculator.results.chart.cumulative"),
    year: t("calculator.results.chart.year"),
  };

  const faqItems = (t("calculator.faq.items", { returnObjects: true }) as FAQItem[]) ?? [];

  // German meta is inlined: the German locale file loads lazily, so the
  // first render would otherwise fall back to the English title.
  const title = locale === "de"
    ? "Dividenden-Rechner 2026 – Netto & Wiederanlage | PieFolio AI"
    : i18n.getFixedT(locale)("calculator.meta.title");
  const description = locale === "de"
    ? "Kostenloser Dividenden-Rechner 2026 mit Wiederanlage (DRIP) und Netto-Dividenden für 200+ Länder. Depotwert, Yield-on-Cost und Dividenden pro Jahr."
    : i18n.getFixedT(locale)("calculator.meta.description");
  const canonical = CANONICAL[locale];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: t("calculator.meta.appName"),
    url: canonical,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web, iOS, Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://getpiefolio.com/" },
      { "@type": "ListItem", position: 2, name: "Tools", item: CANONICAL.en },
      { "@type": "ListItem", position: 3, name: title, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <Helmet>
        <html lang={i18n.language} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={CANONICAL.en} />
        <link rel="alternate" hrefLang="de" href={CANONICAL.de} />
        <link rel="alternate" hrefLang="x-default" href={CANONICAL.en} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24">
        {/* Hero */}
        <header className="container mx-auto px-6 lg:px-12 max-w-4xl text-center py-12">
          <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight mb-5">
            {t("calculator.hero.h1Prefix")}{" "}
            <span className="text-gradient-sage">{t("calculator.hero.h1Year")}</span>
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground mb-8">
            {t("calculator.hero.subhead")}
          </p>
          <div className="flex justify-center">
            <AppStoreBadges variant="dark" size="md" align="center" location="dividend_calculator_hero" />
          </div>
        </header>

        {/* Calculator — stacked vertically (form on top, results below) */}
        <section className="container mx-auto px-6 lg:px-12 max-w-4xl pb-8 space-y-6">
          {/* Inputs card */}
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 shadow-sm">
            <h2 className="text-xl lg:text-2xl font-bold mb-6">
              {t("calculator.form.heading")}
            </h2>
            <CalculatorForm state={state} onChange={setState} labels={formLabels} />
          </div>

          {/* Headline stats — full-width above the chart */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat
              label={t("calculator.results.totalContrib")}
              value={fmt.format(result.totalContributed)}
            />
            <Stat
              label={t("calculator.results.finalValue")}
              value={fmt.format(result.finalValue)}
              accent
            />
            <Stat
              label={t("calculator.results.totalNet")}
              value={fmt.format(result.totalNetDividends)}
            />
          </div>

          {/* Chart card */}
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 shadow-sm">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <h2 className="text-xl lg:text-2xl font-bold">
                {t("calculator.results.heading")}
              </h2>
              <button
                type="button"
                onClick={handleCsv}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                {t("calculator.cta.downloadCsv")}
              </button>
            </div>
            <ResultsChart rows={result.rows} currency={currency} labels={chartLabels} />
          </div>

          {/* Year-by-year table card */}
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8 shadow-sm">
            <ResultsTable rows={result.rows} currency={currency} labels={tableLabels} />
          </div>

          {/* Tax note */}
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
            <div className="text-sm font-semibold text-primary mb-1">
              {t("calculator.results.taxNote")} —{" "}
              {state.country === CUSTOM_RATE_CODE
                ? t("calculator.form.customRateOption")
                : nameFor(state.country)}
            </div>
            <p className="text-sm text-foreground/75">
              {t("calculator.results.taxApplied", {
                pct: pctFmt.format(effectiveRate * 100),
              })}
              {rateInfo.isEstimate && state.country !== CUSTOM_RATE_CODE
                ? ` ${t("calculator.taxTable.estimateNote")}`
                : ""}
            </p>
          </div>
        </section>


        {/* How it works */}
        <section className="container mx-auto px-6 lg:px-12 max-w-3xl py-16">
          <h2 className="text-3xl font-extrabold tracking-tight mb-6">
            {t("calculator.how.heading")}
          </h2>
          <div className="space-y-4 text-foreground/80 leading-relaxed">
            <p>{t("calculator.how.p1")}</p>
            <p>{t("calculator.how.p2")}</p>
            <p>{t("calculator.how.p3")}</p>
          </div>
        </section>

        {/* Tax-rates SEO table — compact, scrollable. Kept for long-tail SEO
            (country × dividend-tax-rate text remains crawlable) but visually
            collapsed so it doesn't dominate the page. */}
        <section className="container mx-auto px-6 lg:px-12 max-w-3xl py-10">
          <details className="rounded-xl border border-border bg-card group">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-4 py-3">
              <div>
                <h2 className="text-base font-bold">{t("calculator.taxTable.heading")}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("calculator.taxTable.intro")}
                </p>
              </div>
              <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none shrink-0">
                +
              </span>
            </summary>
            <div className="max-h-72 overflow-y-auto border-t border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-start">
                      {t("calculator.taxTable.country")}
                    </th>
                    <th className="px-3 py-2 font-semibold text-end">
                      {t("calculator.taxTable.rate")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {REGION_ORDER.flatMap((region) =>
                    DIVIDEND_TAX_RATES.filter((c) => c.region === region)
                      .sort((a, b) =>
                        nameFor(a.code).localeCompare(nameFor(b.code), i18n.language)
                      )
                      .map((c) => (
                        <tr key={c.code} className="border-t border-border/40">
                          <td className="px-3 py-1.5">{nameFor(c.code)}</td>
                          <td className="px-3 py-1.5 tabular-nums text-end text-muted-foreground">
                            {pctFmt.format(c.rate * 100)}%
                            {c.isEstimate ? ` · ${t("calculator.form.estLabel")}` : ""}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </details>
        </section>


        <FAQ items={faqItems} heading={t("calculator.faq.heading")} />

        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </main>
    </div>
  );
};

const Stat = ({ label, value, accent }: { label: string; value: string; accent?: boolean }) => (
  <div
    className={`rounded-2xl border p-5 shadow-sm transition-colors ${
      accent
        ? "border-primary/40 bg-gradient-to-br from-primary/10 to-primary/5"
        : "border-border bg-card hover:border-primary/30"
    }`}
  >
    <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-2">
      {label}
    </div>
    <div
      className={`text-2xl lg:text-3xl font-extrabold tabular-nums leading-tight ${
        accent ? "text-primary" : "text-foreground"
      }`}
    >
      {value}
    </div>
  </div>
);


export default DividendCalculatorPage;
